import NeptuneElement from '../../NeptuneElement.js';

class Drawer extends NeptuneElement {

    static DRAWER_STATE = ['true', 'false'];
    static DRAWER_TYPE = ['modal', 'standard'];

    templateMarkup = `
        <dialog>
            <aside>
                <header>
                    <slot name="header"></slot>
                    <neptune-button id="closeButton" type="tertiary" size="small" icon-end="fa-solid fa-xmark"></neptune-button>
                </header>
                <div>
                    <slot name="body"></slot>
                </div>
                <footer>
                    <slot name="footer"></slot>
                </footer>
            </aside>
        </dialog>
    `;
    componentCssUrl = '/src/components/container/drawer/drawer.css';

    #dialogElement = null;
    #closeButtonElement = null;
    #handleCloseButtonClickBound = null;
    #handleNavToggleBound = null;
    #handleDrawerOpenBound = null;
    #handleDrawerButtonClickBound = null;
    #handleKeydownBound = null;

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.setEventListeners();
        this.processInitialAttributesAfterRender(Drawer.observedAttributes);
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['open', 'type'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if (oldValue === newValue) {return;}
        switch (name) {
            case 'open':
                this.validatePropertyValue(name, newValue, Drawer.DRAWER_STATE);
                this.#processOpenAttribute(newValue);
                break;
            case 'type':
                this.validatePropertyValue(name, newValue, Drawer.DRAWER_TYPE);
                this.#updateType(newValue);
                break;
            default:
                break;
        }
    }

    #updateType(type) {
        if (!this.#dialogElement) {return;}
        if (type === 'modal') {
            this.#dialogElement.setAttribute('modal', '');
        } else {
            this.#dialogElement.removeAttribute('modal');
        }
    }

    setEventListeners() {
        this.#dialogElement = this.shadowRoot?.querySelector('dialog');
        this.#closeButtonElement = this.shadowRoot?.querySelector('header > neptune-button');

        this.#handleCloseButtonClickBound = this.#handleCloseButtonClick.bind(this);
        this.#handleNavToggleBound = this.#handleNavToggle.bind(this);
        this.#handleDrawerOpenBound = this.#handleDrawerOpen.bind(this);
        this.#handleDrawerButtonClickBound = this.#handleDrawerButtonClick.bind(this);
        this.#handleKeydownBound = this.#handleKeydown.bind(this);

        this.#closeButtonElement?.addEventListener('click', this.#handleCloseButtonClickBound);
        document.addEventListener('neptune-nav-toggle', this.#handleNavToggleBound);
        document.addEventListener('neptune-drawer-open', this.#handleDrawerOpenBound);
        document.addEventListener('click', this.#handleDrawerButtonClickBound);
        document.addEventListener('keydown', this.#handleKeydownBound);
    }

    removeEventListeners() {
        this.#closeButtonElement?.removeEventListener('click', this.#handleCloseButtonClickBound);
        document.removeEventListener('neptune-nav-toggle', this.#handleNavToggleBound);
        document.removeEventListener('neptune-drawer-open', this.#handleDrawerOpenBound);
        document.removeEventListener('click', this.#handleDrawerButtonClickBound);
        document.removeEventListener('keydown', this.#handleKeydownBound);
        this.#dialogElement = null;
        this.#closeButtonElement = null;
        this.#handleCloseButtonClickBound = null;
        this.#handleNavToggleBound = null;
        this.#handleDrawerOpenBound = null;
        this.#handleDrawerButtonClickBound = null;
        this.#handleKeydownBound = null;
    }

    open() {
        this.setAttribute('open', 'true');
    }

    close() {
        this.setAttribute('open', 'false');
    }

    #processOpenAttribute(openValue) {
        if (this.#isOpenValue(openValue)) {
            this.#showDrawer();
        } else {
            this.#hideDrawer();
        }
    }

    #isOpenValue(value) {
        return value === '' || value === 'true';
    }

    #showDrawer() {
        if (!this.#dialogElement || this.#dialogElement.open) {return;}
        const type = this.getAttribute('type');
        if (type === 'modal') {
            this.#dialogElement.showModal();
        } else {
            this.#dialogElement.show();
        }
        this.dispatchEvent(new CustomEvent('neptune-drawer-open', {
            bubbles: true,
            composed: true
        }));
    }

    #hideDrawer() {
        if (!this.#dialogElement || !this.#dialogElement.open) {return;}
        this.#dialogElement.close();
        this.dispatchEvent(new CustomEvent('neptune-drawer-close', {
            bubbles: true,
            composed: true
        }));
    }

    #handleDrawerButtonClick(event) {
        const target = event.target;
        if (!target.matches('[data-open-drawer]')) {return;}

        const drawerSelector = target.getAttribute('data-open-drawer');
        const drawer = document.querySelector(drawerSelector);
        if (drawer?.tagName.toLowerCase() === 'neptune-drawer') {
            drawer.open();
        }
    }

    #handleDrawerOpen(event) {
        const openedDrawer = event.target;
        document.querySelectorAll('neptune-drawer').forEach(drawer => {
            if (drawer !== openedDrawer) {
                drawer.close();
            }
        });
        const navElement = document.querySelector('neptune-nav');
        navElement?.setAttribute('state', 'rail');
        document.body.setAttribute('data-nav-state', 'rail');
    }

    #handleCloseButtonClick() {
        this.close();
    }

    #handleKeydown(event) {
        if (event.key === 'Escape' && this.#dialogElement?.open) {
            this.close();
        }
    }

    #handleNavToggle(event) {
        const state = event?.detail?.state;
        if (state && state !== 'rail') {
            this.close();
        }
    }
}

customElements.define('neptune-drawer', Drawer);
export default Drawer;